'use client';

/**
 * FUNNEL / OVERVIEW room — Control Room v4 project-room spec, VERBATIM styles:
 *   · KPI row: 4 bare 27px numbers w/ mono labels + hover halo (t4Kpis)
 *   · 3D stage bars: gradient glass columns on floor-pad ellipses w/ drop %
 *     between stages (t4Stages, heights 210→50 spec table)
 *   · four 340px side panels (t4 panel chrome: r12, gradient glass, scanlines)
 * Data: same /api/funnels/[id]/stats + /api/improve/overview as classic.
 */
import { useEffect, useState } from 'react';
import type { BusinessProject } from '@/lib/business-projects';
import { EmptyState, mono } from '@/future/primitives';

const H = [210, 150, 110, 74, 50]; // spec stage-bar heights

const panelStyle: React.CSSProperties = {
  boxSizing: 'border-box', borderRadius: 12, padding: 16,
  background: 'linear-gradient(170deg,rgba(40,140,180,.08),rgba(8,18,28,.4) 55%)',
  border: '1px solid rgba(125,232,255,.26)', boxShadow: '0 0 40px -12px rgba(0,240,255,.2)',
  overflow: 'hidden', position: 'relative',
};
const Scan = () => <div style={{ position: 'absolute', inset: 0, background: 'repeating-linear-gradient(180deg,rgba(140,235,255,.05) 0 1px,transparent 1px 3px)', mixBlendMode: 'screen', opacity: .5, pointerEvents: 'none' }} />;
const PTitle = ({ t }: { t: string }) => <div style={{ ...mono(16, '.24em'), color: '#49677a' }}>{t}</div>;

export function FunnelSurface({ project }: { project: BusinessProject | null }) {
  const [stats, setStats] = useState<any>(null);
  const [improve, setImprove] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!project?.funnelId) { setLoading(false); return; }
    let live = true;
    const load = () => {
      fetch(`/api/funnels/${project.funnelId}/stats?range=30d`)
        .then(r => r.json())
        .then(d => { if (live) { setStats(d?.stats || null); setLoading(false); } })
        .catch(() => setLoading(false));
      fetch('/api/improve/overview').then(r => r.json()).then(d => live && setImprove(d)).catch(() => {});
    };
    load();
    const i = setInterval(load, 60_000); // live-stats law: 60s
    return () => { live = false; clearInterval(i); };
  }, [project?.funnelId]);

  if (!project?.funnelId) return <EmptyState what="funnel" action="NO FUNNEL WIRED TO THIS PROJECT" />;
  if (loading) return <div className="py-16 text-center" style={{ ...mono(17, '.3em'), color: 'var(--uif-t5)' }}>SYNCING FUNNEL TELEMETRY…</div>;
  if (!stats) return <EmptyState what="funnel data" action="STATS ENDPOINT UNREACHABLE" />;

  const ov = stats.overview || {};
  const leads = ov.leads || {}; const appts = ov.appointments || {}; const views = ov.videoViews || {};
  const funnel = (stats.funnel?.stages || []).slice(0, 5);
  const conv = leads.last30Days ? ((appts.last30Days || appts.booked || 0) / leads.last30Days * 100).toFixed(1) : null;
  const kpis = [
    { l: 'LEADS · 30D', v: leads.last30Days ?? leads.total ?? '—', d: `${leads.last7Days ?? 0} · 7D`, up: (leads.last7Days ?? 0) > 0 },
    { l: 'BOOKED CALLS', v: appts.booked ?? appts.total ?? '—', d: `${appts.cancellationRate ?? 0}% CXL`, up: (appts.cancellationRate ?? 0) < 20 },
    { l: 'VIDEO VIEWS', v: views.total ?? '—', d: `${views.last7Days ?? 0} · 7D`, up: (views.last7Days ?? 0) > 0 },
    { l: 'LEAD → BOOKED', v: conv ? `${conv}%` : '—', d: '30D', up: true },
  ];
  const pages = (stats.traffic?.byPage || []).slice(0, 5);
  const maxPv = Math.max(...pages.map((p: any) => p.pageviews), 1);
  const videos = (stats.videos?.videos || []).slice(0, 5);
  const by = improve?.byStatus || {};
  const maxSt = Math.max(by.proposed || 0, by.approved || 0, by.done || 0, 1);
  const signals = [
    { n: 'VIEWS LAST 7D', m: String(views.last7Days ?? 0) },
    { n: 'LEADS TODAY', m: String(leads.today ?? 0) },
    { n: 'CANCEL RATE', m: `${appts.cancellationRate ?? 0}%` },
    { n: 'COMPLETION', m: `${stats.videos?.totals?.completionRate ?? 0}%` },
  ];

  return (
    <div className="relative h-full min-h-0">
      {/* KPI row — t4Kpis verbatim */}
      <div style={{ position: 'absolute', left: 0, right: 0, top: 8, display: 'flex', justifyContent: 'center', gap: 64 }}>
        {kpis.map(k => (
          <div key={k.l} style={{ textAlign: 'center', padding: '10px 16px', margin: '-10px -16px', borderRadius: 12, transition: 'background .25s' }}>
            <div style={{ ...mono(16, '.24em'), color: '#49677a', marginBottom: 5 }}>{k.l}</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, justifyContent: 'center' }}>
              <span style={{ fontWeight: 200, fontSize: 54, color: '#aef0ff', textShadow: '0 0 16px rgba(0,240,255,.35)' }}>{k.v}</span>
              <span style={{ ...mono(18), color: k.up ? '#5df2b4' : '#ff7d8a' }}>{k.d}</span>
            </div>
          </div>
        ))}
      </div>

      {/* stage bars — t4Stages verbatim (scaled ×.85 to fit 900-high viewport) */}
      {funnel.length > 0 && (
        <div style={{ position: 'absolute', left: 380, right: 380, top: 190, height: 250, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          {funnel.map((st: any, i: number) => {
            const h = Math.round((H[i] ?? 50) * .85);
            const prev = funnel[i - 1]?.count || 0;
            const drop = i > 0 && prev > 0 ? `-${Math.max(0, 100 - Math.round(st.count / prev * 100))}%` : '';
            return (
              <div key={i} style={{ position: 'relative', width: 120, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', gap: 6 }}>
                {drop && <span style={{ position: 'absolute', left: -26, bottom: 150, ...mono(17, '.1em'), color: '#7fa9bd' }}>{drop}</span>}
                <span style={{ ...mono(28, '.02em'), color: '#aef0ff', textShadow: '0 0 14px rgba(0,240,255,.4)' }}>{st.count?.toLocaleString?.() ?? st.count}</span>
                <span style={{ display: 'block', width: 64, height: h, background: 'linear-gradient(180deg,rgba(158,240,255,.55),rgba(158,240,255,.06))', borderTop: '1px solid rgba(190,245,255,.95)', boxShadow: '0 0 24px rgba(0,240,255,.25)', transition: 'height .5s cubic-bezier(.25,.9,.3,1)' }} />
                <span style={{ width: 110, height: 24, borderRadius: '50%', border: '1px solid rgba(125,232,255,.35)', background: 'radial-gradient(closest-side,rgba(0,240,255,.16),transparent 72%)', marginTop: -6 }} />
                <span style={{ ...mono(15, '.2em'), color: '#49677a', whiteSpace: 'nowrap' }}>{(st.label || st.name || `STAGE ${i + 1}`).toUpperCase().slice(0, 18)}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* left panels */}
      <div style={{ ...panelStyle, position: 'absolute', left: 0, top: 96, width: 340, height: 226 }}>
        <Scan />
        <PTitle t={`TRAFFIC · 30D — ${stats.traffic?.totals?.pageviews ?? 0} PV · ${stats.traffic?.totals?.uniqueSessions ?? 0} SESSIONS`} />
        {pages.map((p: any) => (
          <div key={p.page} style={{ marginTop: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 }}>
              <span style={{ fontWeight: 400, fontSize: 24, color: '#d7f2ff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>/{p.page}</span>
              <span style={{ ...mono(17), color: '#49677a', whiteSpace: 'nowrap' }}>{p.pageviews} · {p.uniqueSessions}U</span>
            </div>
            <div style={{ marginTop: 6, height: 2, background: 'rgba(125,232,255,.08)' }}>
              <div style={{ height: 2, width: `${Math.max(2, p.pageviews / maxPv * 100)}%`, background: 'linear-gradient(90deg,rgba(158,240,255,.9),rgba(158,240,255,.1))', boxShadow: '0 0 8px rgba(0,240,255,.4)' }} />
            </div>
          </div>
        ))}
      </div>
      <div style={{ ...panelStyle, position: 'absolute', left: 0, top: 342, width: 340, height: 190 }}>
        <Scan />
        <PTitle t="IMPROVE PIPELINE" />
        {[{ l: 'PROPOSED', v: by.proposed || 0, c: '#ffc36b' }, { l: 'APPROVED', v: by.approved || 0, c: '#9ef0ff' }, { l: 'DONE · 30D', v: by.done || 0, c: '#5df2b4' }].map(r => (
          <div key={r.l} style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 }}>
              <span style={{ ...mono(16, '.18em'), color: '#7fa9bd' }}>{r.l}</span>
              <span style={{ ...mono(20), color: r.c }}>{r.v}</span>
            </div>
            <div style={{ marginTop: 7, height: 2, background: 'rgba(125,232,255,.08)' }}>
              <div style={{ height: 2, width: `${Math.max(3, r.v / maxSt * 100)}%`, background: r.c, boxShadow: `0 0 8px ${r.c}` }} />
            </div>
          </div>
        ))}
      </div>

      {/* right panels */}
      <div style={{ ...panelStyle, position: 'absolute', right: 0, top: 96, width: 340, height: 226 }}>
        <Scan />
        <PTitle t={`VIDEO ENGAGEMENT — ${stats.videos?.totals?.totalViews ?? 0} VIEWS`} />
        {videos.map((v: any) => (
          <div key={`${v.videoPage}-${v.videoId}`} style={{ marginTop: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 }}>
              <span style={{ fontWeight: 400, fontSize: 24, color: '#d7f2ff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v.videoId}</span>
              <span style={{ ...mono(17), color: '#49677a', whiteSpace: 'nowrap' }}>{v.totalViews}V · {v.avgCompletion}%</span>
            </div>
            <div style={{ marginTop: 6, height: 2, background: 'rgba(125,232,255,.08)' }}>
              <div style={{ height: 2, width: `${Math.min(100, Math.max(2, v.avgCompletion || 0))}%`, background: 'linear-gradient(90deg,rgba(93,242,180,.9),rgba(93,242,180,.15))', boxShadow: '0 0 8px rgba(93,242,180,.4)' }} />
            </div>
          </div>
        ))}
      </div>
      <div style={{ ...panelStyle, position: 'absolute', right: 0, top: 342, width: 340, height: 190 }}>
        <Scan />
        <PTitle t="SIGNALS" />
        {signals.map(s => (
          <div key={s.n} style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 18 }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'rgba(158,240,255,.7)', boxShadow: '0 0 8px rgba(0,240,255,.5)', flex: 'none', alignSelf: 'center' }} />
            <span style={{ flex: 1, fontWeight: 400, fontSize: 24, color: '#d7f2ff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.n}</span>
            <span style={{ ...mono(16, '.06em'), color: '#7fa9bd', whiteSpace: 'nowrap' }}>{s.m}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
